import { NativeAudio } from '@capacitor-community/native-audio';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { env } from '@config/env';
import { platform } from '@shared/lib/platform';

/**
 * NOC alarm. Plays a looped `alarm.mp3` for as long as the dashboard has
 * unacknowledged incidents.
 *
 * Two implementations, picked at runtime:
 *
 *  - **Native (Capacitor iOS / Android)** → `@capacitor-community/native-audio`.
 *    On iOS this routes audio through `AVAudioSession` with the `.playback`
 *    category, so the alarm **plays over the hardware silent switch and
 *    Control Center mute** — which is what the NOC requires.
 *
 *  - **Web (Vite dev server + production web deploy)** → `HTMLAudioElement`
 *    with `loop = true`. Wired with autoplay-unlock + auto-resume guards
 *    described below.
 *
 * Both paths share the public API: {@link startPanicAlarmLoop} /
 * {@link stopPanicAlarmLoop} / {@link installAlarmUnlocker}. The dashboard
 * doesn't care which underlies it.
 *
 * The asset lives at `public/alarm.mp3` so it's served at `/alarm.mp3` on
 * web and bundled into the native app at `public/alarm.mp3` (where the
 * native-audio plugin expects it) by `cap sync`.
 */

const ASSET_ID = 'panic-alarm';
const NATIVE_ASSET_PATH = 'public/alarm.mp3';
const WEB_URL = '/alarm.mp3';

const HAPTIC_INTERVAL_MS = 1500;
const RESUME_DELAY_MS = 60;

let webAudioEl: HTMLAudioElement | null = null;
let nativePreloaded = false;
let unlockerInstalled = false;
let hapticHandle: ReturnType<typeof setInterval> | null = null;
/** Single source of truth — should the alarm currently be sounding? */
let shouldBePlaying = false;

/* ── Web path ──────────────────────────────────────────────────────── */

function getWebAudio(): HTMLAudioElement | null {
  if (webAudioEl) return webAudioEl;
  if (typeof Audio === 'undefined') {
    console.warn('[alarm] Audio constructor not available');
    return null;
  }
  try {
    const el = new Audio(WEB_URL);
    el.loop = true;
    el.preload = 'auto';
    el.volume = 0.9;

    // iOS Safari compatibility (irrelevant on Capacitor native, which uses
    // the plugin path — but kept here for mobile web testing).
    el.setAttribute('playsinline', '');
    el.setAttribute('webkit-playsinline', '');
    el.style.display = 'none';
    if (typeof document !== 'undefined' && document.body) {
      document.body.appendChild(el);
    }
    try {
      el.load();
    } catch {
      /* harmless on older browsers */
    }

    // Resilience: if anything pauses the audio while we still want it
    // playing, resume on the next tick.
    el.addEventListener('pause', () => {
      if (!shouldBePlaying) return;
      setTimeout(() => {
        if (shouldBePlaying && el.paused) {
          el.play().catch((e) => {
            console.warn('[alarm] auto-resume failed:', e);
          });
        }
      }, RESUME_DELAY_MS);
    });

    webAudioEl = el;
    return el;
  } catch (e) {
    console.warn('[alarm] web audio init failed:', e);
    return null;
  }
}

/* ── Native path ───────────────────────────────────────────────────── */

async function preloadNative(): Promise<boolean> {
  if (nativePreloaded) return true;
  try {
    await NativeAudio.preload({
      assetId: ASSET_ID,
      assetPath: NATIVE_ASSET_PATH,
      audioChannelNum: 1,
      isUrl: false,
      volume: 0.9,
    });
    nativePreloaded = true;
    console.info('[alarm] native audio preloaded');
    return true;
  } catch (e) {
    // Idempotent: the plugin throws if the asset is already loaded.
    if (e instanceof Error && /already|exist/i.test(e.message)) {
      nativePreloaded = true;
      return true;
    }
    console.warn('[alarm] native preload failed:', e);
    return false;
  }
}

/* ── Public API ────────────────────────────────────────────────────── */

/**
 * Wire a one-shot user-gesture listener that unlocks the (web) audio
 * element. On native this also kicks off the native asset preload so the
 * very first alarm has zero buffering delay.
 *
 * On the web side, the unlocker is aware of {@link shouldBePlaying} so it
 * doesn't silence an already-active alarm just to perform the unlock.
 */
export function installAlarmUnlocker(): void {
  if (platform.isNative()) {
    void preloadNative();
  }

  if (unlockerInstalled || typeof document === 'undefined') return;
  unlockerInstalled = true;

  const unlock = () => {
    if (platform.isNative()) return; // not needed on native
    const a = getWebAudio();
    if (!a) return;

    if (shouldBePlaying) {
      if (a.paused) {
        a.play()
          .then(() => console.info('[alarm] web audio unlocked + resumed'))
          .catch((e) => console.warn('[alarm] unlock-resume play rejected:', e));
      }
      return;
    }

    const prevMuted = a.muted;
    a.muted = true;
    a.play()
      .then(() => {
        a.pause();
        a.currentTime = 0;
        a.muted = prevMuted;
        console.info('[alarm] web audio unlocked by user gesture');
      })
      .catch((e) => {
        a.muted = prevMuted;
        console.warn('[alarm] unlock play() rejected:', e);
      });
  };

  const opts: AddEventListenerOptions = { once: true, capture: true };
  document.addEventListener('pointerdown', unlock, opts);
  document.addEventListener('touchstart', unlock, opts);
  document.addEventListener('keydown', unlock, opts);
}

/**
 * Start the looped panic alarm. Idempotent — calling twice does not stack
 * playback.
 */
export function startPanicAlarmLoop(): void {
  if (!env.alarmSoundEnabled) return;
  shouldBePlaying = true;

  if (platform.isNative()) {
    void (async () => {
      const ok = await preloadNative();
      if (!ok) return;
      try {
        await NativeAudio.loop({ assetId: ASSET_ID });
      } catch (e) {
        console.warn('[alarm] native loop failed:', e);
      }
    })();

    if (hapticHandle === null) {
      Haptics.impact({ style: ImpactStyle.Heavy }).catch(() => undefined);
      hapticHandle = setInterval(() => {
        Haptics.impact({ style: ImpactStyle.Heavy }).catch(() => undefined);
      }, HAPTIC_INTERVAL_MS);
    }
    return;
  }

  // Web
  const a = getWebAudio();
  if (a && a.paused) {
    a.currentTime = 0;
    a.play().catch((e) => {
      console.warn(
        '[alarm] play() rejected — browser may be blocking until a user gesture occurs on the page:',
        e,
      );
    });
  }
}

/** Stop the looped panic alarm. Idempotent. */
export function stopPanicAlarmLoop(): void {
  shouldBePlaying = false;

  if (platform.isNative()) {
    void NativeAudio.stop({ assetId: ASSET_ID }).catch(() => undefined);
  } else if (webAudioEl && !webAudioEl.paused) {
    webAudioEl.pause();
    webAudioEl.currentTime = 0;
  }

  if (hapticHandle !== null) {
    clearInterval(hapticHandle);
    hapticHandle = null;
  }
}

/** True iff the alarm is currently sounding (or, on native, supposed to be). */
export function isPanicAlarmLooping(): boolean {
  if (platform.isNative()) return shouldBePlaying;
  return webAudioEl != null && !webAudioEl.paused;
}
